// @ts-strict-ignore
import { type SagaIterator } from 'redux-saga';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';

import { stringToBoolean } from 'helpers/boolean';
import { deserializeError } from 'helpers/deserialize-error';
import { trimProtocol } from 'helpers/url';
import type { RequestResult } from 'interfaces/api/client';
import { GroupProperty, type IGroupProperties } from 'interfaces/entities/group-property';
import { ApiManager } from 'services/api/api-manager';
import { type GroupDTO } from 'services/api/group/interfaces';
import { GroupActions } from 'store/entities/groups/actions';
import { type IActionWithPayload } from 'store/helper';
import { SettingsViewActionNames } from 'store/views/settings/actions';
import { getSelectedGroupId } from 'store/views/settings/selectors';

import { GroupPropertiesActionNames, GroupPropertiesActions } from './actions';
import { type IFetchGroupPropertiesPayload, type ISaveGroupPropertiesPayload } from './interfaces';

function* fetchCurrentGroupProperties(): SagaIterator {
  const groupId: ReturnType<typeof getSelectedGroupId> = yield select(getSelectedGroupId);
  yield put(GroupPropertiesActions.fetch({ groupId }));
}
function* fetchGroupProperties(action: IActionWithPayload<string, IFetchGroupPropertiesPayload>): SagaIterator {
  const { groupId } = action.payload;
  const { result, error }: RequestResult<IGroupProperties> = yield call(
    ApiManager.groupApi.fetchGroupProperties,
    groupId,
  );

  if (result) {
    yield put(GroupPropertiesActions.fetchSuccess({ groupId, properties: result }));
  } else {
    yield put(GroupPropertiesActions.fetchFailure({ groupId, error: error.message }));
  }
}

/**
 * Prefetches `add_tag_reminder` for every group the agent belongs to, once per session.
 *
 * This mirrors the bulk fetch that used to run on login before the CoffeeScript -> React rewrite
 * dropped it. Keep the value on the group entity instead of the `groupProperties` slice: the endpoint
 * returns only this single property, while that slice is treated elsewhere as the full group config.
 */
export function* prefetchGroupsProperties(): SagaIterator {
  const { result }: RequestResult<Pick<GroupDTO, 'id' | 'properties'>[]> = yield call(
    ApiManager.groupApi.fetchGroupsProperties,
    { prefix: GroupProperty.AddTagReminder },
  );

  if (!result) {
    return;
  }

  yield all(
    result
      .filter((group) => !!group.properties)
      .map((group) =>
        put(
          GroupActions.groupUpdated({
            id: group.id.toString(),
            addTagReminder: stringToBoolean(group.properties.add_tag_reminder),
          }),
        ),
      ),
  );
}

function* saveGroupProperties(action: IActionWithPayload<string, ISaveGroupPropertiesPayload>): SagaIterator {
  const { groupId, properties: rawProperties } = action.payload;
  const { [GroupProperty.ChatWindowLogoPath]: logo, ...properties } = rawProperties;

  const responses: RequestResult[] = yield all([
    Object.keys(properties).length > 0 &&
      call(ApiManager.groupApi.saveGroupProperties, groupId, properties as Partial<IGroupProperties>),
    logo && call(ApiManager.groupApi.saveGroupLogo, groupId, trimProtocol(logo)),
  ]);

  const error = responses
    .filter((response) => !!response?.error)
    .map(deserializeError)
    .join('. ');

  if (!error) {
    yield put(
      GroupPropertiesActions.saveSuccess({
        groupId,
        properties: logo ? { [GroupProperty.ChatWindowLogoPath]: logo, ...properties } : properties,
      }),
    );
  } else {
    yield put(GroupPropertiesActions.saveFailure({ error }));
  }
}

export function* groupPropertiesSagas(): SagaIterator {
  yield takeEvery('APP_READY', prefetchGroupsProperties);
  yield takeEvery(SettingsViewActionNames.SET_SELECTED_GROUP_ID, fetchCurrentGroupProperties);
  yield takeEvery(GroupPropertiesActionNames.FETCH, fetchGroupProperties);
  yield takeEvery(GroupPropertiesActionNames.SAVE, saveGroupProperties);
}
